import { useApiMutation, useApiQuery } from "@gladis/api-client";
import type { MemberDto, MemberListDto } from "@gladis/shared";
import { useState, type ReactNode } from "react";
import { Avatar, Button } from "@gladis/ui";
import { formatDate, errorSentence, useT } from "@gladis/ui/i18n";

/**
 * Who is in this household (AC-H5, FR-001).
 *
 * The list every later feature reads from — savings ownership, card holders, expense splits — so it
 * is one component with one query key rather than each screen assembling its own idea of who is
 * here.
 *
 * WHAT IS AND IS NOT DECIDED HERE. The interface hides Remove on the Owner's row and on the
 * viewer's own, using `role` and the server-computed `isSelf`. That is presentation. Every one of
 * those refusals also exists in `MembershipService.canRemove`, and the tests exercise the request
 * rather than the button — because a hidden control is not an enforced rule, and the two must not
 * be confused.
 */
export function MembersList({ householdId }: { householdId: string }): ReactNode {
  const t = useT();
  const members = useApiQuery<MemberListDto>(
    ["members", householdId],
    `/households/${householdId}/members`,
  );

  if (members.isPending) {
    return (
      <section className="rounded-control border border-line bg-panel p-5">
        <h2 className="text-xl font-semibold">{t("identity.members.heading")}</h2>
        <p className="mt-3 text-muted">{t("identity.loading")}</p>
      </section>
    );
  }

  if (members.error) {
    return (
      <section className="rounded-control border border-line bg-panel p-5">
        <h2 className="text-xl font-semibold">{t("identity.members.heading")}</h2>
        <p className="mt-3 text-muted" role="alert">
          {errorSentence(members.error)}
        </p>
      </section>
    );
  }

  const rows = members.data?.members ?? [];

  return (
    <section className="rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">{t("identity.members.heading")}</h2>
      <p className="mt-1 text-sm text-muted">{t("identity.members.hint")}</p>

      <ul className="mt-4 divide-y divide-line">
        {rows.map((member) => (
          <MemberRow key={member.userId} householdId={householdId} member={member} />
        ))}
      </ul>
    </section>
  );
}

function MemberRow({ householdId, member }: { householdId: string; member: MemberDto }): ReactNode {
  const t = useT();
  const [confirming, setConfirming] = useState(false);

  const remove = useApiMutation<void, void>(
    (client) => client.delete(`/households/${householdId}/members/${member.userId}`),
    {
      invalidates: [["members", householdId]],
      onSuccess: () => setConfirming(false),
    },
  );

  // The Owner is immune to removal by anyone (AC-H5), and nobody removes themselves — that is the
  // Leave flow, which lives further down the page and has different rules.
  const removable = member.role !== "owner" && !member.isSelf;

  return (
    <li className="py-3">
      <div className="flex items-baseline justify-between gap-4">
        {/*
          `items-center` on the inner row rather than `items-baseline`, because an avatar has no
          baseline to align a two-line block against — the picture would sit level with the name and
          hang below the email. The outer row keeps its baseline alignment for the controls.
        */}
        <div className="flex min-w-0 items-center gap-3">
          <Avatar src={member.avatarUrl} name={member.displayName} size="md" />
          <div className="min-w-0">
            <p className="truncate">
              {member.displayName}
              {member.isSelf ? <span className="ml-2 text-sm text-muted">you</span> : null}
            </p>
            <p className="truncate text-sm text-muted">{member.email}</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <span className="text-sm text-muted">
            {member.role === "owner" ? t("identity.members.owner") : t("identity.members.admin")}
          </span>
          <span className="hidden text-sm text-muted sm:inline">
            joined {formatDate(member.joinedAt)}
          </span>
          {removable ? (
            <Button type="button" onClick={() => setConfirming(true)}>
              {t("common.remove")}
            </Button>
          ) : null}
        </div>
      </div>

      {confirming ? (
        <div className="mt-3 rounded-control border border-line p-4" role="alert">
          <p>
            {t("common.remove")}
            <strong>{member.displayName}</strong> from this household?
          </p>
          {/* The retention policy, stated at the moment it applies. Someone removing a member
              usually wants to know whether the expenses that member entered are about to vanish —
              and the answer is no (AC-H10). */}
          <p className="mt-2 text-sm text-muted">{t("identity.members.removeConsequence")}</p>

          <div className="mt-4 flex gap-3">
            <Button
              variant="primary"
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              {remove.isPending ? t("common.removing") : `Remove ${member.displayName}`}
            </Button>
            <Button type="button" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </Button>
          </div>

          {remove.error ? (
            <p className="mt-3 text-sm text-muted" role="alert">
              {errorSentence(remove.error)}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
